import React from "react";
import {
  education,
  professionalCertificates,
  courses,
  languages,
} from "../../mock";
import {
  GraduationCap,
  BookOpen,
  Languages as LangIcon,
  Calendar,
  BadgeCheck,
} from "lucide-react";

const tagColor = {
  Cloud: "from-sky-400/20 to-sky-400/5 text-sky-300 border-sky-400/30",
  GenAI: "from-emerald-400/20 to-emerald-400/5 text-emerald-300 border-emerald-400/30",
  AI: "from-violet-400/20 to-violet-400/5 text-violet-300 border-violet-400/30",
  ML: "from-fuchsia-400/20 to-fuchsia-400/5 text-fuchsia-300 border-fuchsia-400/30",
  Python: "from-amber-400/20 to-amber-400/5 text-amber-300 border-amber-400/30",
};

export default function Education() {
  return (
    <section
      id="education"
      className="relative py-24 md:py-32 border-t border-zinc-900 bg-[#0a0a0c] overflow-hidden"
    >
      <div className="aurora top-10 -right-40 opacity-20" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 md:px-8">
        <div className="flex items-center gap-3 mb-3 reveal">
          <span className="mono text-xs section-num font-semibold">03 //</span>
          <span className="mono text-xs text-zinc-500 uppercase tracking-[0.2em]">
            Education & Certifications
          </span>
        </div>
        <h2 className="display text-4xl md:text-5xl font-semibold reveal">
          Lifelong learner<span className="text-zinc-500">...</span>
        </h2>

        {/* Education + Languages */}
        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {education.map((ed, i) => (
            <div
              key={ed.id}
              className="card-hover lg:col-span-2 border border-zinc-900 rounded-2xl p-6 md:p-7 bg-[#0b0b0d] reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-[#5eead4]/10 text-[#5eead4] grid place-items-center border border-[#5eead4]/20 shrink-0">
                  <GraduationCap size={22} />
                </div>
                <div className="flex-1">
                  <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                    Education
                  </div>
                  <h3 className="display text-xl md:text-2xl text-white mt-1">
                    {ed.degree}
                  </h3>
                  <div className="text-sm text-zinc-400 mt-1">
                    <span className="text-[#5eead4]">{ed.institution}</span>
                    <span className="mx-2 text-zinc-700">·</span>
                    <span className="inline-flex items-center gap-1 text-zinc-500">
                      <Calendar size={12} /> {ed.period}
                    </span>
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 mono text-xs text-[#5eead4] border border-[#5eead4]/30 bg-[#5eead4]/5 rounded-full px-3 py-1.5">
                    {ed.detail}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div
            className="card-hover border border-zinc-900 rounded-2xl p-6 bg-[#0b0b0d] reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-md bg-[#5eead4]/10 text-[#5eead4] grid place-items-center border border-[#5eead4]/20">
                <LangIcon size={16} />
              </div>
              <div className="display text-lg text-white">Languages</div>
            </div>
            <div className="space-y-4">
              {languages.map((l) => (
                <div key={l.name}>
                  <div className="flex items-center justify-between mono text-xs">
                    <span className="text-zinc-300">{l.name}</span>
                    <span className="text-[#5eead4]">{l.level}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-zinc-900 overflow-hidden">
                    <div
                      className="bar-fill h-full rounded-full bg-gradient-to-r from-[#5eead4] to-[#a78bfa]"
                      style={{ "--bar": l.percent / 100 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Professional Certificates + Courses */}
        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <CertColumn
            title="Professional Certificates"
            subtitle="Industry-recognized professional credentials"
            icon={BadgeCheck}
            accent="text-[#5eead4]"
            ringColor="border-[#5eead4]/30 bg-[#5eead4]/5"
            items={professionalCertificates}
          />

          <CertColumn
            title="Courses"
            subtitle="Specialized training programs and online courses"
            icon={BookOpen}
            accent="text-[#a78bfa]"
            ringColor="border-[#a78bfa]/30 bg-[#a78bfa]/5"
            items={courses}
          />
        </div>
      </div>
    </section>
  );
}

function CertColumn({ title, subtitle, icon: Icon, accent, ringColor, items }) {
  return (
    <div className="reveal">
      <div className="flex items-center gap-3 mb-5">
        <div
          className={`h-9 w-9 rounded-md ${ringColor} ${accent} grid place-items-center border`}
        >
          <Icon size={16} />
        </div>
        <div>
          <div className="display text-lg text-white flex items-center gap-2">
            {title}
            <span className="mono text-[11px] text-zinc-500">
              ({items.length})
            </span>
          </div>
          <div className="mono text-[11px] text-zinc-500">{subtitle}</div>
        </div>
      </div>

      <div className="grid gap-3">
        {items.map((c, i) => {
          const colors =
            tagColor[c.tag] ||
            "from-zinc-400/10 to-zinc-400/5 text-zinc-300 border-zinc-700";
          return (
            <div
              key={c.id}
              className="card-hover relative border border-zinc-900 rounded-xl p-5 bg-[#0b0b0d] reveal"
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <div
                className={`absolute top-3 right-3 mono text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 bg-gradient-to-br ${colors}`}
              >
                {c.tag}
              </div>
              <div className="mono text-[11px] text-zinc-500 inline-flex items-center gap-1">
                <Calendar size={11} /> {c.date}
              </div>
              <h4 className="display text-base text-white mt-3 leading-snug pr-12">
                {c.title}
              </h4>
              <p className="text-xs text-zinc-500 mt-2">{c.issuer}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
